import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { api } from './api.ts';
import { useAuth } from './auth.tsx';
import { PasskeySignInButton } from './passkey.tsx';
import { AuthenticatorKey } from './AuthenticatorKey.tsx';
import { ErrorBox, Field, Notice, useAction } from './ui.tsx';
import { isRmsSite } from './site.ts';
import { BlueLogo } from './BlueMark.tsx';

export function AuthShell({ children, subtitle }: { children: ReactNode; subtitle?: string }) {
  const rms = isRmsSite();
  return (
    <div className={`auth-shell ${rms ? 'bhq-auth' : ''}`}>
      <aside className="auth-side">
        {rms ? (
          <div className="bhq-pitch">
            <BlueLogo />
            <p className="bhq-tagline">One platform. A safer tomorrow.</p>
            <span className="bhq-rule" />
            <ul className="bhq-pillars" aria-label="What BlueHQ does">
              {['Report', 'Enforce', 'Serve', 'Manage', 'Connect'].map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
            <p className="bhq-serve">Built for those who serve.</p>
          </div>
        ) : (
          <div className="auth-pitch">
            <img src="/civilhound-badge-clear.webp" alt="CivilHound" width={220} height={220} className="auth-badge" />
            <p>Civil process for sheriffs’ offices. From the clerk’s desk to the deputy at the door, and back to the court.</p>
          </div>
        )}
        <footer className="auth-footer">
          <PalmettoMark />
          <div>
            <div><>Developed by <strong>803 Systems, LLC</strong></></div>
            <div>Built in Chester, South Carolina</div>
            <div className="auth-footer-copy">© {new Date().getFullYear()} 803 Systems, LLC</div>
          </div>
        </footer>
      </aside>
      <main className="auth-main">
        <div className="auth-card">
          <h1 className="auth-h">{subtitle ?? 'Sign in'}</h1>
          {children}
        </div>
      </main>
    </div>
  );
}

/** Palmetto tree and crescent, the South Carolina symbol. */
export function PalmettoMark() {
  return (
    <svg className="palmetto" viewBox="0 0 64 64" width="44" height="44" aria-hidden="true">
      <path d="M13 8a10 10 0 1 0 10 14 8.5 8.5 0 1 1-10-14Z" fill="currentColor" />
      <path d="M36 58c1-10 1.2-20 .6-30h3c-.6 10-.4 20 .6 30Z" fill="currentColor" />
      <g fill="currentColor">
        <path d="M38 27c-4-6-11-8-17-6 6 0 11 3 14 8Z" />
        <path d="M38 27c4-6 11-8 17-6-6 0-11 3-14 8Z" />
        <path d="M38 26c-2-7-8-11-14-12 5 2 9 6 11 12Z" />
        <path d="M38 26c2-7 8-11 14-12-5 2-9 6-11 12Z" />
        <path d="M38 26c0-7-2-12-5-15 2 4 3 9 3 15Z" />
        <path d="M38 26c0-7 2-12 5-15-2 4-3 9-3 15Z" />
        <path d="M38 28c-5-2-10-1-14 3 4-2 9-2 13 0Z" />
        <path d="M38 28c5-2 10-1 14 3-4-2-9-2-13 0Z" />
      </g>
    </svg>
  );
}

/** Set when someone signs in; read once by the app to pick their home screen. */
export const JUST_SIGNED_IN = 'ch-just-signed-in';

export function LoginPage() {
  const { refresh } = useAuth();
  const q = new URLSearchParams(location.search);
  const [agencyCode, setAgency] = useState(() => q.get('agency') ?? (() => { try { return localStorage.getItem('civilos.agency') ?? ''; } catch { return ''; } })());
  const [username, setUser] = useState(q.get('username') ?? '');
  const [password, setPw] = useState('');
  const a = useAction();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => {
      await api.post('/api/auth/login', { agencyCode, username, password });
      try {
        localStorage.setItem('civilos.agency', agencyCode.toUpperCase());
        // Tells the app this is a fresh sign-in, so it opens on the right side for the person's job.
        sessionStorage.setItem(JUST_SIGNED_IN, '1');
      } catch {
        /* ignore */
      }
      await refresh();
    });
  };
  return (
    <AuthShell>
      <form onSubmit={submit} className="stack">
        <Field label="Agency code">
          <input value={agencyCode} onChange={(e) => setAgency(e.target.value.toUpperCase())} autoComplete="organization" required placeholder="e.g. EXCO" />
        </Field>
        <Field label="Username">
          <input value={username} onChange={(e) => setUser(e.target.value)} autoComplete="username" required />
        </Field>
        <Field label="Password">
          <input type="password" value={password} onChange={(e) => setPw(e.target.value)} autoComplete="current-password" required />
        </Field>
        <ErrorBox error={a.error} />
        <button className="btn primary" disabled={a.busy}>
          {a.busy ? 'Signing in…' : 'Sign in'}
        </button>
        <PasskeySignInButton onSignedIn={refresh} />
        {q.get('welcome') && <Notice tone="ok">Your account is ready. Sign in with your new password.</Notice>}
        <a className="small" href={`/forgot${agencyCode ? `?agency=${encodeURIComponent(agencyCode)}` : ''}`}>
          Forgot your password?
        </a>
        <p className="muted small">Each agency has separate credentials. To work for another agency, sign out and sign in with that agency’s account.</p>
      </form>
    </AuthShell>
  );
}

export function MfaPage() {
  const { me, refresh, logout } = useAuth();
  const [code, setCode] = useState('');
  const [enroll, setEnroll] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const a = useAction();
  const needsEnroll = !me?.user.totpEnabled;
  return (
    <AuthShell subtitle="Two-factor verification">
      <div className="stack">
        {needsEnroll && !enroll && (
          <>
            <Notice>Your role requires two-factor authentication. Set up an authenticator app to continue.</Notice>
            <button className="btn primary" onClick={() => a.run(async () => setEnroll(await api.post('/api/auth/mfa/enroll')))}>
              Set up authenticator
            </button>
          </>
        )}
        {enroll && (
          <AuthenticatorKey secret={enroll.secret} otpauthUri={enroll.otpauthUri} />
        )}
        {(!needsEnroll || enroll) && (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              a.run(async () => {
                await api.post('/api/auth/mfa/verify', { code });
                await refresh();
              });
            }}
          >
            <Field label="6-digit code">
              <input inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} required autoFocus />
            </Field>
            <button className="btn primary" disabled={a.busy || code.length !== 6}>
              Verify
            </button>
          </form>
        )}
        <ErrorBox error={a.error} />
        <button className="btn link" onClick={logout}>
          Cancel and sign out
        </button>
      </div>
    </AuthShell>
  );
}

export function PasswordResetPage() {
  const { refresh, logout } = useAuth();
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const a = useAction();
  return (
    <AuthShell subtitle="Choose a new password">
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => {
            await api.post('/api/auth/password', { currentPassword: cur, newPassword: next });
            await refresh();
          });
        }}
      >
        <Notice>An administrator set a temporary password. Choose a new one (12+ characters, letters and numbers).</Notice>
        <Field label="Current password">
          <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required />
        </Field>
        <Field label="New password">
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} minLength={12} required />
        </Field>
        <ErrorBox error={a.error} />
        <button className="btn primary" disabled={a.busy}>
          Save password
        </button>
        <button type="button" className="btn link" onClick={logout}>
          Sign out
        </button>
      </form>
    </AuthShell>
  );
}

const go = (to: string) => {
  location.href = to;
};

/** /forgot — email me a reset link. */
export function ForgotPage() {
  const q = new URLSearchParams(location.search);
  const [agencyCode, setAgency] = useState(q.get('agency') ?? '');
  const [login, setLogin] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const a = useAction();
  return (
    <AuthShell subtitle="Reset your password">
      {done ? (
        <div className="stack">
          <Notice tone="ok">{done}</Notice>
          <p className="small muted">No email? Check spam, or ask your agency administrator to send you a reset link.</p>
          <a href="/signin">Back to sign in</a>
        </div>
      ) : (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            a.run(async () => setDone((await api.post<{ message: string }>('/api/auth/forgot', { agencyCode, login })).message));
          }}
        >
          <Field label="Agency code">
            <input value={agencyCode} onChange={(e) => setAgency(e.target.value.toUpperCase())} required placeholder="e.g. EXCO" />
          </Field>
          <Field label="Username or email">
            <input value={login} onChange={(e) => setLogin(e.target.value)} required autoComplete="username" />
          </Field>
          <ErrorBox error={a.error} />
          <button className="btn primary" disabled={a.busy}>
            Email me a reset link
          </button>
          <a className="small" href="/signin">
            Back to sign in
          </a>
        </form>
      )}
    </AuthShell>
  );
}

function NewPasswordFields({ pw, setPw, pw2, setPw2 }: { pw: string; setPw: (s: string) => void; pw2: string; setPw2: (s: string) => void }) {
  return (
    <>
      <Field label="New password" hint="12+ characters with letters and numbers">
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" required minLength={12} />
      </Field>
      <Field label="Type it again">
        <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" required />
      </Field>
      {pw2 && pw !== pw2 && <span className="small bad-text">The two passwords don’t match.</span>}
    </>
  );
}

/** /reset/:token — set a new password from the emailed link. */
export function ResetLinkPage({ token }: { token: string }) {
  const [info, setInfo] = useState<any>(null);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const a = useAction();
  const load = useAction();
  useEffect(() => void load.run(async () => setInfo(await api.get(`/api/auth/reset/${token}`))), [token]);
  return (
    <AuthShell subtitle="Set a new password">
      {!info ? (
        load.error ? (
          <div className="stack">
            <ErrorBox error={load.error} />
            <a href="/forgot">Get a new link</a>
          </div>
        ) : (
          <p className="muted">Checking your link…</p>
        )
      ) : (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            a.run(async () => {
              const r = await api.post<{ agencyCode: string; username: string }>(`/api/auth/reset/${token}`, { password: pw });
              go(`/signin?agency=${encodeURIComponent(r.agencyCode)}&username=${encodeURIComponent(r.username)}&welcome=1`);
            });
          }}
        >
          <Notice>
            {info.displayName} — <strong className="mono">{info.username}</strong> at {info.agency} ({info.agencyCode})
          </Notice>
          <NewPasswordFields pw={pw} setPw={setPw} pw2={pw2} setPw2={setPw2} />
          <ErrorBox error={a.error} />
          <button className="btn primary" disabled={a.busy || pw.length < 12 || pw !== pw2}>
            Set password
          </button>
          <p className="small muted">This signs you out everywhere. You’ll still need your authenticator code to sign in.</p>
        </form>
      )}
    </AuthShell>
  );
}

/** /invite/:token — accept an invitation: pick a username and password. */
export function InviteAcceptPage({ token }: { token: string }) {
  const [info, setInfo] = useState<any>(null);
  const [username, setUsername] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const a = useAction();
  const load = useAction();
  useEffect(
    () =>
      void load.run(async () => {
        const i = await api.get<any>(`/api/public/invites/${token}`);
        setInfo(i);
        setUsername(i.suggestedUsername);
      }),
    [token],
  );
  return (
    <AuthShell subtitle="Set up your account">
      {!info ? (
        load.error ? <ErrorBox error={load.error} /> : <p className="muted">Checking your invitation…</p>
      ) : (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            a.run(async () => {
              const r = await api.post<{ agencyCode: string; username: string }>(`/api/public/invites/${token}/accept`, { username: username.trim(), password: pw });
              go(`/signin?agency=${encodeURIComponent(r.agencyCode)}&username=${encodeURIComponent(r.username)}&welcome=1`);
            });
          }}
        >
          <Notice>
            Welcome, <strong>{info.displayName}</strong>. You’re joining <strong>{info.agency}</strong> as {info.roleLabel}.
          </Notice>
          <Field label="Agency code" hint="You’ll type this every time you sign in.">
            <input value={info.agencyCode} readOnly className="mono" />
          </Field>
          <Field label="Pick a username" hint="Letters, numbers, dots, dashes">
            <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" required pattern="[A-Za-z0-9._-]{3,100}" />
          </Field>
          <NewPasswordFields pw={pw} setPw={setPw} pw2={pw2} setPw2={setPw2} />
          <ErrorBox error={a.error} />
          <button className="btn primary" disabled={a.busy || pw.length < 12 || pw !== pw2 || username.trim().length < 3}>
            Create my account
          </button>
        </form>
      )}
    </AuthShell>
  );
}
